import axios from 'axios';
import {action, computed, observable, runInAction} from 'mobx';
import * as config from '../config';
import {SnackReporter} from '../snack/SnackManager';
import {ISyncNode, ISyncNodeRuntime} from '../types';
import translate from '../i18n/translator';

export interface SyncNodePayload {
    name: string;
    type: string;
    remark?: string;
    address?: string;
    sshUser?: string;
    sshPort?: number;
    authType?: string;
    credentialRef?: string;
    tags?: string[];
    metadata?: Record<string, unknown>;
}

export type SyncNodeUpdatePayload = SyncNodePayload;

export class SyncNodeStore {
    @observable
    private nodes: ISyncNode[] = [];

    @observable
    private localRuntime: ISyncNodeRuntime | null = null;

    @observable
    public loading = false;

    @observable
    public saving = false;

    public constructor(
        private readonly snack: SnackReporter,
        private readonly tokenProvider: () => string
    ) {}

    private get headers() {
        return {'X-GoHook-Key': this.tokenProvider()};
    }

    @computed
    public get all(): ISyncNode[] {
        return this.nodes;
    }

    @computed
    public get local(): ISyncNodeRuntime | null {
        return this.localRuntime;
    }

    @action
    public clear() {
        this.nodes = [];
        this.localRuntime = null;
        this.loading = false;
        this.saving = false;
    }

    @action
    public applyRuntimeUpdate = (nodeId: number, runtime: ISyncNodeRuntime) => {
        const idx = this.nodes.findIndex((n) => n.id === nodeId);
        if (idx === -1) return;
        this.nodes = this.nodes.map((n) =>
            n.id === nodeId
                ? {
                      ...n,
                      runtime: {
                          ...(n.runtime || {}),
                          ...(runtime || {}),
                      },
                  }
                : n
        );
    };

    @action
    public applyLocalRuntimeUpdate = (runtime: ISyncNodeRuntime) => {
        this.localRuntime = {
            ...(this.localRuntime || {}),
            ...(runtime || {}),
        };
    };

    @action
    public refreshNodes = async () => {
        this.loading = true;
        try {
            const [nodesResponse, localResponse] = await Promise.allSettled([
                axios.get<ISyncNode[]>(`${config.get('url')}api/sync/nodes`, {
                    headers: this.headers,
                }),
                axios.get<ISyncNodeRuntime>(`${config.get('url')}api/sync/local-runtime`, {
                    headers: this.headers,
                }),
            ]);
            if (nodesResponse.status === 'fulfilled') {
                runInAction(() => {
                    this.nodes = nodesResponse.value.data || [];
                });
            } else {
                this.handleError(nodesResponse.reason, translate('syncNodes.snack.loadFailed'));
                throw nodesResponse.reason;
            }
            if (localResponse.status === 'fulfilled') {
                runInAction(() => {
                    this.localRuntime = localResponse.value.data || null;
                });
            } else {
                runInAction(() => {
                    this.localRuntime = null;
                });
            }
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    };

    @action
    public createNode = async (payload: SyncNodePayload): Promise<ISyncNode | undefined> => {
        this.saving = true;
        try {
            const response = await axios.post<ISyncNode>(
                `${config.get('url')}api/sync/nodes`,
                payload,
                {
                    headers: this.headers,
                }
            );
            this.snack(translate('syncNodes.snack.createSuccess'));
            await this.refreshNodes();
            return response.data;
        } catch (error: unknown) {
            this.handleError(error, translate('syncNodes.snack.createFailed'));
            throw error;
        } finally {
            runInAction(() => {
                this.saving = false;
            });
        }
    };

    @action
    public updateNode = async (id: number, payload: SyncNodeUpdatePayload) => {
        this.saving = true;
        try {
            await axios.put(`${config.get('url')}api/sync/nodes/${id}`, payload, {
                headers: this.headers,
            });
            this.snack(translate('syncNodes.snack.updateSuccess'));
            await this.refreshNodes();
        } catch (error: unknown) {
            this.handleError(error, translate('syncNodes.snack.updateFailed'));
            throw error;
        } finally {
            runInAction(() => {
                this.saving = false;
            });
        }
    };

    @action
    public deleteNode = async (id: number) => {
        this.saving = true;
        try {
            await axios.delete(`${config.get('url')}api/sync/nodes/${id}`, {
                headers: this.headers,
            });
            this.snack(translate('syncNodes.snack.deleteSuccess'));
            await this.refreshNodes();
        } catch (error: unknown) {
            this.handleError(error, translate('syncNodes.snack.deleteFailed'));
            throw error;
        } finally {
            runInAction(() => {
                this.saving = false;
            });
        }
    };

    @action
    public rotateToken = async (id: number): Promise<ISyncNode> => {
        this.saving = true;
        try {
            const response = await axios.post<ISyncNode>(
                `${config.get('url')}api/sync/nodes/${id}/rotate-token`,
                {},
                {headers: this.headers}
            );
            this.snack(translate('syncNodes.snack.rotateSuccess'));
            await this.refreshNodes();
            return response.data;
        } catch (error: unknown) {
            this.handleError(error, translate('syncNodes.snack.rotateFailed'));
            throw error;
        } finally {
            runInAction(() => {
                this.saving = false;
            });
        }
    };

    @action
    public resetPairing = async (id: number): Promise<ISyncNode> => {
        this.saving = true;
        try {
            const response = await axios.post<ISyncNode>(
                `${config.get('url')}api/sync/nodes/${id}/reset-pairing`,
                {},
                {headers: this.headers}
            );
            this.snack(translate('syncNodes.snack.resetSuccess'));
            await this.refreshNodes();
            return response.data;
        } catch (error: unknown) {
            this.handleError(error, translate('syncNodes.snack.resetFailed'));
            throw error;
        } finally {
            runInAction(() => {
                this.saving = false;
            });
        }
    };

    @action
    public triggerInstall = async (id: number, payload?: {sshUser?: string; sshPort?: number}) => {
        this.saving = true;
        try {
            await axios.post(`${config.get('url')}api/sync/nodes/${id}/install`, payload ?? {}, {
                headers: this.headers,
            });
            this.snack(translate('syncNodes.snack.installStarted'));
            await this.refreshNodes();
        } catch (error: unknown) {
            this.handleError(error, translate('syncNodes.snack.installFailed'));
            throw error;
        } finally {
            runInAction(() => {
                this.saving = false;
            });
        }
    };

    private handleError(error: unknown, fallback: string) {
        const detail =
            (error as {response?: {data?: {error?: string; message?: string}}})?.response?.data
                ?.error ||
            (error as {response?: {data?: {message?: string}}})?.response?.data?.message ||
            (error instanceof Error ? error.message : '');
        this.snack(detail ? `${fallback}: ${detail}` : fallback);
    }
}
